import React, { useCallback, useEffect, useState } from 'react';
import type {
  InsuranceCardExtractionResponse,
  PatientCoverage,
} from '@features/patient-coverages';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutlined';
import {
  Box,
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  type SelectChangeEvent,
  TextField,
  Typography,
} from '@mui/material';

import { type InsuranceInfo, type PatientAddress } from '../../types/types';

import AddressCard from './AddressCard';
import InsuranceCardUpload from './InsuranceCardUpload';
import PayerSearch from './PayerSearch';

/**
 * Union type for both legacy and new coverage formats
 * Allows gradual migration from InsuranceInfo to PatientCoverage
 */
type CoverageData = InsuranceInfo | PatientCoverage;

interface InsuranceCardProps {
  insurance: CoverageData | null | undefined;
  onChange: (insurance: CoverageData | null) => void;
  onBlur?: () => void;
  title?: string;
  showDelete?: boolean;
  onDelete?: () => void;
  errors?: Record<string, string>;
  highlightedFields?: string[];
  disabled?: boolean;
  patientId?: string;
}

/**
 * Helper to check if coverage is new format (PatientCoverage)
 */
function isPatientCoverage(data: CoverageData): data is PatientCoverage {
  return 'patient_insurance_coverage_id' in data;
}

const InsuranceCard: React.FC<InsuranceCardProps> = ({
  insurance,
  onChange,
  onBlur,
  title = 'Insurance Information',
  showDelete = false,
  onDelete,
  errors = {},
  highlightedFields = [],
  disabled = false,
  patientId,
}) => {
  const [showInsuredDetails, setShowInsuredDetails] = useState(false);
  const [subscriberFirstName, setSubscriberFirstName] = useState('');
  const [subscriberLastName, setSubscriberLastName] = useState('');

  // Get relationship field - handles both old and new formats
  const relationshipField = isPatientCoverage(insurance || {})
    ? (insurance as PatientCoverage).relationship_to_subscriber
    : (insurance as InsuranceInfo)?.relationship_to_insured;

  useEffect(() => {
    // Show insured/subscriber details if relationship is not 'self'
    if (relationshipField && relationshipField !== 'self') {
      setShowInsuredDetails(true);
    }

    // Initialize subscriber name fields from combined insured_name (legacy format)
    if (insurance && !isPatientCoverage(insurance)) {
      const insuredName = insurance.insured_name || '';
      const nameParts = insuredName.split(' ');
      setSubscriberFirstName(nameParts[0] || '');
      setSubscriberLastName(nameParts.slice(1).join(' ') || '');
    } else if (insurance && isPatientCoverage(insurance)) {
      setSubscriberFirstName(insurance.subscriber_first_name || '');
      setSubscriberLastName(insurance.subscriber_last_name || '');
    }
  }, [relationshipField, insurance]);

  const handleFieldChange = (
    field: string,
    value: string | null | boolean | PatientAddress,
  ) => {
    const updatedInsurance: CoverageData = {
      ...(insurance || {}),
      [field]: value === '' ? null : value,
    };
    onChange(updatedInsurance);
  };

  const handlePayerSelect = (payer: { id: string; name: string; eligibility: string } | null) => {
    const isNewFormat = insurance && isPatientCoverage(insurance);
    const consentKey = isNewFormat ? 'consent_on_file' : 'authorization_on_file';
    
    if (payer) {
      const updatedInsurance: CoverageData = {
        ...(insurance || {}),
        payer_id: payer.id,
        payer_name: payer.name,
        // Auto-check authorization when payer is selected
        [consentKey]: true,
      };
      onChange(updatedInsurance);
    } else {
      const updatedInsurance: CoverageData = {
        ...(insurance || {}),
        payer_id: null,
        payer_name: null,
      };
      onChange(updatedInsurance);
    }
    
    // Trigger immediate save for payer selection
    onBlur?.();
  };

  const handleRelationshipChange = (event: SelectChangeEvent) => {
    const value = event.target.value;
    const isNewFormat = insurance && isPatientCoverage(insurance);
    const relationshipKey = isNewFormat ? 'relationship_to_subscriber' : 'relationship_to_insured';

    handleFieldChange(relationshipKey, value);

    // Show/hide insured/subscriber details based on relationship
    if (value !== 'self' && value !== '') {
      setShowInsuredDetails(true);
    } else {
      setShowInsuredDetails(false);
      // Clear insured/subscriber fields if relationship is self
      if (value === 'self') {
        if (isNewFormat) {
          const updatedInsurance: PatientCoverage = {
            ...(insurance),
            relationship_to_subscriber: 'self',
            subscriber_first_name: null,
            subscriber_last_name: null,
            subscriber_dob: null,
            subscriber_sex: null,
            subscriber_address: null,
          };
          onChange(updatedInsurance);
        } else {
          const updatedInsurance: InsuranceInfo = {
            ...(insurance as InsuranceInfo),
            relationship_to_insured: 'self',
            insured_name: null,
            insured_dob: null,
            insured_sex: null,
            insured_address: null,
          };
          onChange(updatedInsurance);
        }
        setSubscriberFirstName('');
        setSubscriberLastName('');
      }
    }
    
    // Trigger immediate save for select changes
    onBlur?.();
  };

  const handleSubscriberAddressChange = (address: PatientAddress | null) => {
    const isNewFormat = insurance && isPatientCoverage(insurance);
    const addressKey = isNewFormat ? 'subscriber_address' : 'insured_address';
    handleFieldChange(addressKey, address);
  };

  const handleSubscriberNameChange = (firstName: string, lastName: string) => {
    setSubscriberFirstName(firstName);
    setSubscriberLastName(lastName);

    if (insurance && isPatientCoverage(insurance)) {
      const updated: PatientCoverage = {
        ...(insurance),
        subscriber_first_name: firstName || null,
        subscriber_last_name: lastName || null,
      };
      onChange(updated);
    } else {
      const updated: InsuranceInfo = {
        ...(insurance as InsuranceInfo),
        insured_name: `${firstName} ${lastName}`.trim() || null,
      };
      onChange(updated);
    }
  };

  // Get field values - handles both old and new formats
  const payer_id = insurance?.payer_id || null;
  const payer_name = insurance?.payer_name || null;
  const plan_name = insurance && 'plan_name' in insurance ? insurance.plan_name : null;
  const member_id = isPatientCoverage(insurance || {})
    ? (insurance as PatientCoverage)?.member_id
    : (insurance as InsuranceInfo)?.policy_number;
  const group_number = insurance && 'group_number' in insurance ? insurance.group_number : null;
  const consent_on_file = isPatientCoverage(insurance || {})
    ? (insurance as PatientCoverage)?.consent_on_file
    : (insurance as InsuranceInfo)?.authorization_on_file;
  const subscriber_dob = isPatientCoverage(insurance || {})
    ? (insurance as PatientCoverage)?.subscriber_dob
    : (insurance as InsuranceInfo)?.insured_dob;
  const subscriber_sex = isPatientCoverage(insurance || {})
    ? (insurance as PatientCoverage)?.subscriber_sex
    : (insurance as InsuranceInfo)?.insured_sex;
  const subscriber_address = isPatientCoverage(insurance || {})
    ? (insurance as PatientCoverage)?.subscriber_address
    : (insurance as InsuranceInfo)?.insured_address;

  const selectedPayer =
    payer_id && payer_name ? { id: payer_id, name: payer_name, eligibility: '' } : null;

  const handleCardExtracted = useCallback(
    (data: InsuranceCardExtractionResponse) => {
      const isNewFormat = insurance && isPatientCoverage(insurance);
      const updated: CoverageData = {
        ...(insurance || {}),
        payer_name: data.payer_name ?? undefined,
        plan_name: data.plan_name ?? undefined,
        group_number: data.group_number ?? undefined,
        effective_date: data.effective_date ?? undefined,
        ...(isNewFormat
          ? {
              member_id: data.member_id ?? undefined,
              relationship_to_subscriber: data.subscriber_relationship ?? undefined,
              subscriber_first_name: data.subscriber_first_name ?? undefined,
              subscriber_last_name: data.subscriber_last_name ?? undefined,
            }
          : {
              policy_number: data.member_id ?? undefined,
              relationship_to_insured: data.subscriber_relationship ?? undefined,
              insured_name:
                data.subscriber_first_name || data.subscriber_last_name
                  ? `${data.subscriber_first_name || ''} ${data.subscriber_last_name || ''}`.trim()
                  : undefined,
            }),
      };
      onChange(updated);
      onBlur?.();
    },
    [insurance, onChange, onBlur],
  );

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
          {title}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {patientId && (
            <InsuranceCardUpload
              patientId={patientId}
              onExtracted={handleCardExtracted}
              disabled={disabled}
            />
          )}
          {showDelete && onDelete && (
            <IconButton size="small" onClick={onDelete} color="error" disabled={disabled}>
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          )}
        </Box>
      </Box>

      <Grid container spacing={2}>
        {/* Payer Search */}
        <Grid size={{ xs: 12 }}>
          <PayerSearch
            onPayerSelect={handlePayerSelect}
            disabled={disabled}
            value={selectedPayer}
          />
          {/* Show Payer ID as simple label if present */}
          {insurance?.payer_id && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
              Payer ID: {insurance.payer_id}
            </Typography>
          )}
        </Grid>

        {/* Plan Name */}
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            label="Plan Name (Optional)"
            value={plan_name || ''}
            onChange={e => handleFieldChange('plan_name', e.target.value)}
            onBlur={onBlur}
            variant="outlined"
            size="small"
            fullWidth
            disabled={disabled}
            error={!!errors.plan_name}
            helperText={errors.plan_name}
          />
        </Grid>

        {/* Member/Policy Number */}
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            label="Member/Policy ID Number"
            value={member_id || ''}
            onChange={e => {
              const fieldName = isPatientCoverage(insurance || {})
                ? 'member_id'
                : 'policy_number';
              handleFieldChange(fieldName, e.target.value);
            }}
            onBlur={onBlur}
            variant="outlined"
            size="small"
            fullWidth
            disabled={disabled}
            error={!!errors.policy_number || !!errors.member_id}
            helperText={errors.policy_number || errors.member_id}
            sx={{
              ...((highlightedFields.includes('policy_number') ||
                highlightedFields.includes('member_id')) && {
                '& .MuiOutlinedInput-root': {
                  '& fieldset': {
                    borderColor: 'primary.main',
                    borderWidth: 2,
                  },
                },
              }),
            }}
          />
        </Grid>

        {/* Group Number */}
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            label="Group Number (Optional)"
            value={group_number || ''}
            onChange={e => handleFieldChange('group_number', e.target.value)}
            onBlur={onBlur}
            variant="outlined"
            size="small"
            fullWidth
            disabled={disabled}
            error={!!errors.group_number}
            helperText={errors.group_number}
          />
        </Grid>

        {/* Relationship to Subscriber/Insured */}
        <Grid size={{ xs: 12, sm: 6 }}>
          <FormControl fullWidth size="small" disabled={disabled}>
            <InputLabel>Relationship to Subscriber</InputLabel>
            <Select
              value={relationshipField || ''}
              onChange={handleRelationshipChange}
              onBlur={onBlur}
              label="Relationship to Subscriber"
              sx={{
                ...((highlightedFields.includes('relationship_to_insured') ||
                  highlightedFields.includes('relationship_to_subscriber')) && {
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': {
                      borderColor: 'primary.main',
                      borderWidth: 2,
                    },
                  },
                }),
              }}
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              <MenuItem value="self">Self</MenuItem>
              <MenuItem value="spouse">Spouse</MenuItem>
              <MenuItem value="child">Child</MenuItem>
              <MenuItem value="other">Other</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        {/* Consent/Authorization on File */}
        <Grid size={{ xs: 12, sm: 6 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={consent_on_file || false}
                onChange={e => {
                  const fieldName = isPatientCoverage(insurance || {})
                    ? 'consent_on_file'
                    : 'authorization_on_file';
                  handleFieldChange(fieldName, e.target.checked);
                  // Trigger immediate save for checkbox changes
                  onBlur?.();
                }}
                disabled={disabled}
              />
            }
            label="Consent/Authorization Signature on File"
          />
        </Grid>

        {/* Subscriber/Insured Person Details (if different from patient) */}
        {showInsuredDetails && (
          <>
            <Grid size={{ xs: 12 }}>
              <Divider sx={{ my: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Subscriber Information
                </Typography>
              </Divider>
            </Grid>

            {/* Handle both formats: split first/last for new, combined for legacy */}
            {isPatientCoverage(insurance || {}) ? (
              <>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    label="Subscriber First Name"
                    value={subscriberFirstName}
                    onChange={e => handleSubscriberNameChange(e.target.value, subscriberLastName)}
                    onBlur={onBlur}
                    variant="outlined"
                    size="small"
                    fullWidth
                    disabled={disabled}
                    error={!!errors.subscriber_first_name}
                    helperText={errors.subscriber_first_name}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    label="Subscriber Last Name"
                    value={subscriberLastName}
                    onChange={e => handleSubscriberNameChange(subscriberFirstName, e.target.value)}
                    onBlur={onBlur}
                    variant="outlined"
                    size="small"
                    fullWidth
                    disabled={disabled}
                    error={!!errors.subscriber_last_name}
                    helperText={errors.subscriber_last_name}
                  />
                </Grid>
              </>
            ) : (
              <Grid size={{ xs: 12, sm: 12 }}>
                <TextField
                  label="Subscriber Name"
                  value={`${subscriberFirstName} ${subscriberLastName}`.trim()}
                  onChange={e => {
                    const parts = e.target.value.split(' ');
                    handleSubscriberNameChange(parts[0] || '', parts.slice(1).join(' ') || '');
                  }}
                  onBlur={onBlur}
                  variant="outlined"
                  size="small"
                  fullWidth
                  disabled={disabled}
                  error={!!errors.insured_name}
                  helperText={errors.insured_name}
                />
              </Grid>
            )}

            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="Subscriber Date of Birth"
                type="date"
                value={subscriber_dob || ''}
                onChange={e => {
                  const fieldName = isPatientCoverage(insurance || {})
                    ? 'subscriber_dob'
                    : 'insured_dob';
                  handleFieldChange(fieldName, e.target.value);
                  // Trigger immediate save for date picker
                  onBlur?.();
                }}
                variant="outlined"
                size="small"
                fullWidth
                disabled={disabled}
                error={!!errors.insured_dob || !!errors.subscriber_dob}
                helperText={errors.insured_dob || errors.subscriber_dob}
               slotProps={{ inputLabel: { shrink: true } }}/>
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth size="small" disabled={disabled}>
                <InputLabel>Subscriber Sex</InputLabel>
                <Select
                  value={subscriber_sex || ''}
                  onChange={e => {
                    const fieldName = isPatientCoverage(insurance || {})
                      ? 'subscriber_sex'
                      : 'insured_sex';
                    handleFieldChange(fieldName, e.target.value);
                    // Trigger immediate save for select changes
                    onBlur?.();
                  }}
                  label="Subscriber Sex"
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  <MenuItem value="Male">Male</MenuItem>
                  <MenuItem value="Female">Female</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Subscriber Address */}
            <Grid size={{ xs: 12 }}>
              <Box sx={{ mt: 1 }}>
                <AddressCard
                  address={subscriber_address || null}
                  onChange={handleSubscriberAddressChange}
                  onBlur={onBlur}
                  title="Subscriber Address"
                  disabled={disabled}
                  errors={errors}
                  highlightedFields={highlightedFields}
                />
              </Box>
            </Grid>
          </>
        )}
      </Grid>
    </Box>
  );
};

export default InsuranceCard;